import React from 'react';
import { BookOpen, CheckSquare, ClipboardCheck, FilePlus2, GitCompare, History, LayoutDashboard, Library, Network, PenLine, ShieldCheck } from 'lucide-react';
import { ResearchDemoProvider, useResearchDemo } from '../../contexts/ResearchDemoContext';
import { syntheticCollectionNotice } from '../../data/researchDemo';
import type { ResearchSection, ResearchWorkspace } from '../../types/researchDemo';
import { DemoShell, type ShellSection } from '../../components/demoShell/DemoShell';
import { WorkspaceChooser } from '../../components/demoShell/WorkspaceChooser';
import { ResearchOverview } from '../../components/researchDemo/ResearchOverview';
import { SourceLibrary } from '../../components/researchDemo/SourceLibrary';
import { KnowledgeMap } from '../../components/researchDemo/KnowledgeMap';
import { CompareStudies } from '../../components/researchDemo/CompareStudies';
import { ResearchNotebook } from '../../components/researchDemo/ResearchNotebook';
import { ResearchHistory } from '../../components/researchDemo/ResearchHistory';
import { ApprovedKnowledge, ReviewQueue } from '../../components/researchDemo/ReviewQueue';
import { ProposeRelationship } from '../../components/researchDemo/ProposeRelationship';
import { EvidenceRecord } from '../../components/researchDemo/EvidenceRecord';
const workspaceLabels: Record<ResearchWorkspace, string> = {
  research: 'Research Workspace',
  curation: 'Curation & Administration'
};
const sectionViews: Record<ResearchSection, React.ComponentType> = {
  overview: ResearchOverview,
  library: SourceLibrary,
  map: KnowledgeMap,
  compare: CompareStudies,
  notebook: ResearchNotebook,
  history: ResearchHistory,
  queue: ReviewQueue,
  propose: ProposeRelationship,
  approved: ApprovedKnowledge
};
export function ResearchDemoPage() {
  return <ResearchDemoProvider data-id="element-2860" data-mp="3q68byew13z7">
      <ResearchDemo data-id="element-2861" data-mp="3t27tr6dw0pr" />
    </ResearchDemoProvider>;
}
function ResearchDemo() {
  const {
    state,
    dispatch,
    persistenceOk
  } = useResearchDemo();
  const workspace = state.ui.workspace;
  const proposedCount = state.claims.filter(claim => claim.status === 'proposed').length;
  if (!workspace) {
    return <WorkspaceChooser domainId="research" eyebrow="NOETIVRA RESEARCH" title="From sources to understanding." description="Explore the evidence. Review the interpretation. Build knowledge you can inspect." persistenceOk={persistenceOk} cards={[{
      id: 'research',
      title: 'Research Workspace',
      body: 'Explore studies, compare findings and follow approved relationships back to their source passages.',
      buttonLabel: 'Enter Research Workspace',
      icon: BookOpen,
      onEnter: () => dispatch({
        type: 'enterWorkspace',
        workspace: 'research'
      })
    }, {
      id: 'curation',
      title: 'Curation & Administration',
      body: 'Inspect proposals, check supporting evidence and publish reviewed relationships with their limitations.',
      buttonLabel: 'Enter Curation Workspace',
      icon: ShieldCheck,
      onEnter: () => dispatch({
        type: 'enterWorkspace',
        workspace: 'curation'
      })
    }]} footnote={<p className="rounded-lg border border-lavender/25 bg-lavender-pale px-4 py-3 text-sm text-ink-dark" data-id="element-2863" data-mp="ja3dk1gmlq40">
            {syntheticCollectionNotice} Live literature ingestion, AI extraction, external knowledge services and secure institutional
            permissions are not connected in this demonstration.
          </p>} data-id="element-2862" data-mp="3aakjpkjc5aan" />;
  }
  const sections: ShellSection[] = workspace === 'research' ? [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'library',
    label: 'Source Library',
    icon: Library
  }, {
    id: 'map',
    label: 'Knowledge Map',
    icon: Network
  }, {
    id: 'compare',
    label: 'Compare Studies',
    icon: GitCompare,
    count: state.ui.compareIds.length
  }, {
    id: 'notebook',
    label: 'Research Notebook',
    icon: PenLine,
    count: state.notes.length
  }, {
    id: 'history',
    label: 'Review History',
    icon: History
  }] : [{
    id: 'queue',
    label: 'Review Queue',
    icon: ClipboardCheck,
    count: proposedCount
  }, {
    id: 'propose',
    label: 'Propose Relationship',
    icon: FilePlus2
  }, {
    id: 'library',
    label: 'Source Library',
    icon: Library
  }, {
    id: 'approved',
    label: 'Approved Knowledge',
    icon: CheckSquare
  }, {
    id: 'history',
    label: 'Review History',
    icon: History
  }];
  const allowed = sections.map(section => section.id);
  const stored = state.ui.lastSection[workspace];
  const current = (allowed.includes(stored) ? stored : allowed[0]) as ResearchSection;
  const openClaim = state.claims.find(claim => claim.id === state.ui.openClaimId);
  const View = sectionViews[current];
  return <DemoShell domainId="research" productLabel="Noetivra Research" workspaceLabel={workspaceLabels[workspace]} sections={sections} current={current} onSelect={id => dispatch({
    type: 'setSection',
    section: id as ResearchSection
  })} onSwitchWorkspace={() => dispatch({
    type: 'enterWorkspace',
    workspace: null
  })} persistenceOk={persistenceOk} extraNotice={`${syntheticCollectionNotice} Interactive prototype · Fictional studies · Saved in this browser.`} data-id="element-2864" data-mp="2wh3am1kzow57">
      {openClaim ? <EvidenceRecord claim={openClaim} data-id="element-2865" data-mp="1usfw0r3y372n" /> : <View data-id="element-2866" data-mp="1zowvghglh7v1" />}
    </DemoShell>;
}